/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  useSelector,
  useDispatch,
} from 'react-redux';

import { uniq } from 'lodash';
import Today from '@mui/icons-material/Today';
import {
  AccessTime,
  Close,
  EventRepeat,
  FormatListBulleted,
} from '@mui/icons-material';
import { blueGrey } from '@mui/material/colors';
import {
  getLocationForTags,
  getBarnHorsesForTags,
} from '../../../utils/tagsHelper';

import {
  generateEventsFromSchedules,
  getContrast,
} from '../../../utils/scheduleHelper';
import { primary } from '../../../utils/brandColors';
import {
  addSchedule,
  addScheduleException,
  updateSchedule,
} from '../../../redux/schedule/actions';
import EventActionsMenu from '../../TaskDetail/EventActionsMenu';
import SelectIcon from '../../../images/unused-undraw-illustrations/undraw_calendar_re_ki49.svg';

import HeaderSection from './components/HeaderSection';
import TasksSection from './components/TasksSection';
import SchedulingSection from './components/SchedulingSection';
import { setGlobalModals } from '../../../redux/app/actions';
import { TASK_MODAL_TYPE } from '../../../redux/app/types';

const startingValidation = {
  eventTemplate: '',
  startDate: '',
  endDate: '',
  assignment: '',
  workflows: '',
};

const AddEditScheduleModal = ({
  open,
  onClose,
  schedule,
  event,
  config,
}) => {
  const dispatch = useDispatch();

  // React statemo
  const now = Moment(new Date()).startOf('hour').add(1, 'hours').toDate();
  const [eventTemplate, setEventTemplate] = React.useState(null);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('')
  const [workflows, setWorkflows] = React.useState([]);
  const [location, setLocation] = React.useState(null);
  const [detailedLocation, setDetailedLocation] = React.useState(null);
  const [barnHorses, setBarnHorses] = React.useState([])
  const [assignment, setAssignment] = React.useState('');
  const [startDate, setStartDate] = React.useState(now);
  const [endDate, setEndDate] = React.useState(now);
  const [duration, setDuration] = React.useState(1800000);
  const [rrule, setRrule] = React.useState('RRULE:FREQ=DAILY;INTERVAL=1');
  const [metadata, setMetadata] = React.useState({});
  const [additionalTags, setAdditionalTags] = React.useState([]);
  const [advanceNotice, setAdvanceNotice] = React.useState(0);
  const [timezoneWarning, setTimezoneWarning] = React.useState(null);
  const [validation, setValidation] = React.useState(startingValidation);
  const [savingError, setSavingError] = React.useState('');
  const [overridePastSchedule, setOverridePastSchedule] = React.useState(false);
  const [completeTasksInPast, setCompleteTasksInPast] = React.useState(false);
  const [openSaveOptionsAnchorEl, setOpenSaveOptionsAnchorEl] = React.useState(false);
  const [selectedSaveOption, setSelectedSaveOption] = React.useState(null);

  // Redux state
  const isFetchingSchedules = useSelector((state) => state.schedule.isFetchingSchedules);
  const {
    selectedBarn,
    barnHorsesMap,
  } = useSelector((state) => state.barn);
  const locationsMap = useSelector((state) => state.location.locationsMap);
  const allEventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const myTimezone = useSelector((state) => state.app.myTimezone);
  const locationTagTypeId = (selectedBarn.barnMetadata && selectedBarn.barnMetadata.locationTagTypeId) || 0;

  // Filter out event templates based on config
  const eventTemplates = (schedule || config.eventTemplateTagTypeFilter === -1)
    ? allEventTemplates.filter((et) => !!et.tags.find((t) => selectedBarn && t.id === selectedBarn.barnMetadata.eventTaskTypeTagId))
    : allEventTemplates.filter((et) => et.id === config.eventTemplateTagTypeFilter
      || et.tags.filter((tag) => tag.id === config.eventTemplateTagTypeFilter).length > 0)

  const getAndSetDataForTags = (scheduleObject, scheduleConfig) => {
    let foundLocation = null;
    if (scheduleObject && scheduleObject.tags) {
      foundLocation = getLocationForTags(scheduleObject.tags || [], locationTagTypeId, locationsMap)
        || locationsMap[scheduleConfig.locationId] || null;
    } else if (scheduleConfig.locationId) {
      foundLocation = locationsMap[scheduleConfig.locationId]
        ? locationsMap[scheduleConfig.locationId][0]
        : null;
    }
    setLocation(foundLocation || null);
    setDetailedLocation((scheduleObject && scheduleObject.location && scheduleObject.location !== ((foundLocation && foundLocation.name) || ''))
      ? scheduleObject.location
      : null);
    if (foundLocation && foundLocation.timezone) {
      setTimezoneWarning(Moment.tz(myTimezone).zoneAbbr() !== Moment.tz(foundLocation.timezone).zoneAbbr() && `Your timezone ${Moment.tz(myTimezone).zoneAbbr()} (${myTimezone}) is different from the timezone at the selected location ${Moment.tz(foundLocation.timezone).zoneAbbr()} (${foundLocation.timezone}).`);
    }

    // look for barn horses and set
    const horseTagTypeId = (selectedBarn.barnMetadata && selectedBarn.barnMetadata.horseTagTypeId) || 0;
    setBarnHorses(getBarnHorsesForTags((scheduleObject && scheduleObject.tags)
      ? scheduleObject.tags
      : scheduleConfig.taskTagIds, horseTagTypeId, barnHorsesMap))
  };

  const handleChangeLocation = (newLocation) => {
    setLocation(newLocation || null);
    if (newLocation && newLocation.timezone) {
      setTimezoneWarning(Moment.tz(myTimezone).zoneAbbr() !== Moment.tz(newLocation.timezone).zoneAbbr() && `Your timezone ${Moment.tz(myTimezone).zoneAbbr()} (${myTimezone}) is different from the timezone at the selected location ${Moment.tz(location.timezone).zoneAbbr()} (${location.timezone}).`);
    }
  };

  const handleSelectEventTemplate = (e) => {
    const selectedEventTemplate = eventTemplates.filter((et) => et.id === e.target.value)[0];
    setEventTemplate(selectedEventTemplate);
    setAdditionalTags(selectedEventTemplate.tags
      .filter((t) => t.tagType.id !== selectedBarn.barnMetadata.horseTagTypeId && t.tagType.id !== selectedBarn.barnMetadata.locationTagTypeId));

    if (!schedule || !schedule.id) {
      setMetadata(selectedEventTemplate.eventDataDefaultValues);
      setAdvanceNotice(parseInt((selectedEventTemplate.eventDataDefaultValues
        && selectedEventTemplate.eventDataDefaultValues.advanceNotice) || 0, 10));
      setName(selectedEventTemplate.name);
      setDescription(selectedEventTemplate.description);
      setDuration(((selectedEventTemplate && selectedEventTemplate.eventDataDefaultValues
        && selectedEventTemplate.eventDataDefaultValues.defaultDuration
        && parseInt(selectedEventTemplate.eventDataDefaultValues.defaultDuration, 10)) || 1800000))
    }
    setWorkflows([]);
  };

  const startNewSchedule = (scheduleDate) => {
    setOverridePastSchedule(true);
    setStartDate(scheduleDate || now);
    setEndDate(scheduleDate || null);
  };

  const setFields = (scheduleObject) => {
    // The end date and rrule fields are always linked to the parent
    // If they change we prompt to update the series
    const repeatScheduleToUse = (scheduleObject && scheduleObject.parentSchedule) || scheduleObject;

    setValidation(startingValidation);
    setSavingError('');
    setOverridePastSchedule(false);
    if (scheduleObject && scheduleObject.id) {
      setEventTemplate(scheduleObject.eventTemplate);
    } else {
      setEventTemplate(null);
    }
    setName((scheduleObject && scheduleObject.id)
      ? scheduleObject.name
      : '');
    setDescription((scheduleObject && scheduleObject.id)
      ? scheduleObject.description
      : '')
    getAndSetDataForTags(scheduleObject, config);
    setWorkflows((scheduleObject && scheduleObject.id)
      ? scheduleObject.workflows
      : []);
    setAssignment((scheduleObject && scheduleObject.id)
      ? scheduleObject.assignment
      : null);
    let passedInStart = now;
    if (config.startDate) {
      // A start date was passed in, if it is set to midnight, place to nearest hour
      if (Moment(config.startDate).hours() <= 0) {
        passedInStart = Moment(config.startDate).set('hour', Moment().hours() + 1).toDate()
      } else {
        passedInStart = Moment(config.startDate).toDate()
      }
    }
    setStartDate((scheduleObject && scheduleObject.id)
      ? new Date((event && event.start) || scheduleObject.startDate)
      : passedInStart);
    if (repeatScheduleToUse && repeatScheduleToUse.id) {
      setEndDate(repeatScheduleToUse.endDate
        ? new Date(repeatScheduleToUse.endDate)
        : null);
    } else {
      setEndDate(passedInStart);
    }
    setDuration((scheduleObject && scheduleObject.id)
      ? scheduleObject.duration
      : config.duration || ((eventTemplate && eventTemplate.eventDataDefaultValues && eventTemplate.eventDataDefaultValues.defaultDuration
        && parseInt(eventTemplate.eventDataDefaultValues.defaultDuration, 10)) || 1800000));
    setRrule((repeatScheduleToUse && repeatScheduleToUse.id)
      ? repeatScheduleToUse.rrule
      : 'RRULE:FREQ=DAILY;INTERVAL=1');
    setMetadata((scheduleObject && scheduleObject.metadata) || {});
    setAdvanceNotice((scheduleObject && scheduleObject.advanceNotice) || 0);
    setCompleteTasksInPast(false);

    // if event template is null and only one is passed in for options, default select it.
    if (!(scheduleObject && scheduleObject.id) && eventTemplates && eventTemplates.length === 1) {
      handleSelectEventTemplate({ target: { value: eventTemplates[0].id } });
    } else if (scheduleObject && scheduleObject.tags) {
      // since schedule exists, we should filter out the horse and or location tags, those will come in the workflows object
      setAdditionalTags(scheduleObject.tags
        .filter((t) => t.tagType.id !== selectedBarn.barnMetadata.horseTagTypeId && t.tagType.id !== selectedBarn.barnMetadata.locationTagTypeId));
    }
    if (config && config.rescheduleDate) {
      startNewSchedule(config.rescheduleDate);
    }
  };

  React.useEffect(() => {
    setFields(schedule);
  }, [schedule, open, config]);

  const validateFields = () => {
    const updatedValidation = {
      eventTemplate: '',
      startDate: '',
      endDate: '',
      assignment: '',
      workflows: '',
      location: '',
    };
    let isValid = true;

    if (eventTemplate === null) {
      updatedValidation.eventTemplate = 'Please select a calendar type.';
      isValid = false;
    }
    if (workflows.length > 0) {
      for (let i = 0; i < workflows.length; i += 1) {
        if (workflows[i].eventTemplateId === null || workflows[i].eventTemplateId <= 0
          || workflows[i].tagIds === null || workflows[i].tagIds.length === 0) {
          updatedValidation.workflows = 'Required';
          isValid = false;
        }
      }
    }
    // If schedule is starting in past, don't allow more than 3 recurrences
    // (TODO change backedn process to handle task creation in background to get around this)
    if ((!schedule || !schedule.id) && new Date(startDate).getTime() < new Date() && rrule !== null) {
      const retroEvents = generateEventsFromSchedules([{
        startDate,
        endDate: Moment(new Date()).startOf('day').toDate(),
        id: -1,
        tags: [],
        workflows,
        metadata: {},
        eventTemplate,
        rrule: rrule.replace('RRULE:', ''),
      }], [], startDate, endDate || new Date(), {}, {})
      if (retroEvents.length > 3) {
        updatedValidation.startDate = 'Cannot create a recurring calendar events starting in the past. Please individually create retroactive events.';
        isValid = false;
      }
    }

    // Don't allow changing an already started schedule to the past
    if (schedule && schedule.id && new Date(startDate).getTime() < new Date()) {
      updatedValidation.startDate = 'Cannot change the start date of an already scheduled task to a time in the past.';
      isValid = false;
    }

    if (!!endDate && endDate.getTime() < startDate.getTime()) {
      updatedValidation.endDate = 'The event end date must be after the start date.';
      isValid = false;
    }
    if (!location) {
      updatedValidation.location = 'A location is required for calendar events.';
      isValid = false;
    }

    setValidation(updatedValidation);
    return isValid;
  };

  const validateOccurrenceChangeFields = () => {
    const updatedValidation = {
      startDate: '',
      endDate: '',
      assignment: '',
      workflows: '',
      location: '',
    };
    let isValid = true;

    if (workflows.length > 0) {
      for (let i = 0; i < workflows.length; i += 1) {
        if (workflows[i].eventTemplateId === null || workflows[i].eventTemplateId <= 0
          || workflows[i].tagIds === null || workflows[i].tagIds.length === 0) {
          updatedValidation.workflows = 'Required';
          isValid = false;
        }
      }
    }
    // don't validate start date if this is an existing schedule started in past, field cannot be modified
    if (startDate.getTime() < new Date().getTime()) {
      updatedValidation.startDate = 'The event start date and time must be provided and in the future.';
      isValid = false;
    }
    if (!!endDate && endDate.getTime() < startDate.getTime()) {
      updatedValidation.endDate = 'The event end date must be after the start date.';
      isValid = false;
    }
    if (!location) {
      updatedValidation.location = 'A location is required for calendar events.';
      isValid = false;
    }

    setValidation(updatedValidation);
    return isValid;
  };

  const scheduleRepeatChange = () => {
    // Returns true is the end date or rrule changed for a repeating task
    const repeatScheduleToUse = (schedule && schedule.parentSchedule) || schedule;

    if (repeatScheduleToUse && repeatScheduleToUse.id && ((repeatScheduleToUse.endDate !== null && endDate === null)
      || (repeatScheduleToUse.endDate === null && endDate !== null)
      || (repeatScheduleToUse.endDate !== null && endDate !== null
        && new Date(repeatScheduleToUse.endDate).getTime() !== new Date(endDate).getTime())
      || (repeatScheduleToUse.rrule !== rrule))) {
      return true;
    }
    return false;
  }

  const getScheduleChangeData = () => {
    const isNewSchedule = !schedule || !schedule.id || overridePastSchedule;
    const isSingleOccurrenceSchedule = schedule && schedule.id && !schedule.parentSchedule && schedule.endDate != null
      && new Date(schedule.startDate).getTime() === new Date(schedule.endDate).getTime();
    const isRepeatSeriesChange = scheduleRepeatChange();
    const isChangingFromSeriesToSingleOccurrence = schedule && schedule.id && !isSingleOccurrenceSchedule
      && event && event.start && new Date(event.start).getTime() === new Date(endDate).getTime();
    const parentScheduleObject = (schedule && schedule.parentSchedule) || schedule;
    const isHoursChange = (!parentScheduleObject || !parentScheduleObject.id)
      || Moment(startDate).hours() !== Moment(parentScheduleObject.startDate).hours();
    const isMinutesChange = (!parentScheduleObject || !parentScheduleObject.id)
      || Moment(startDate).minutes() !== Moment(parentScheduleObject.startDate).minutes();

    return {
      isNewSchedule,
      isSingleOccurrenceSchedule,
      isRepeatSeriesChange,
      isChangingFromSeriesToSingleOccurrence,
      parentScheduleObject,
      isTimeChange: isHoursChange || isMinutesChange,
    }
  }

  const handleSaveEntireSchedule = () => {
    setOpenSaveOptionsAnchorEl(false);
    if (!validateFields()) { return; }

    const scheduleChangeData = getScheduleChangeData();

    const request = {
      startDate,
      endDate,
      duration,
      assignment: assignment || '',
      name: (name && name.length >= 2)
        ? name
        : eventTemplate.name,
      description: description || eventTemplate.description,
      tags: uniq([
        ...(workflows.map((w) => w.tagIds).flat()),
        ...additionalTags.map((et) => et.id),
        location.pingumTagId,
        ...barnHorses.map((bh) => bh.pingumTagId)]),
      workflows,
      location: detailedLocation || location.name,
      overrideCancellations: false, // TODO implement/change this
      metadata,
      advanceNotice,
      timezone: location.timezone,
      type: 'Invitation',
      completeTasksInPast,
      createTasksInPast: true,
    };

    // Adding new schedule
    if (scheduleChangeData.isNewSchedule) {
      dispatch(addSchedule(eventTemplate.id, {
        ...request,
        rrule: rrule.replace('RRULE:', ''),
      })).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to add the event. Please contact Questri support.');
        console.error(error);
      });
    } else {
      let promise = null;
      // modify entire series
      promise = dispatch(updateSchedule((schedule.parentSchedule && schedule.parentSchedule.id) || schedule.id, {
        ...request,
        startDate: ((scheduleChangeData.isSingleOccurrenceSchedule || scheduleChangeData.isChangingFromSeriesToSingleOccurrence) && startDate)
          || (scheduleChangeData.isTimeChange && Moment(scheduleChangeData.parentScheduleObject.startDate)
            .set({
              hour: Moment(startDate).hours(),
              minute: Moment(startDate).minutes(),
            }).toDate())
          || null,
        rrule: rrule === schedule.rrule
          ? null
          : rrule.replace('RRULE:', ''),
      }));
      promise.then(() => {
        const refreshData = scheduleRepeatChange() || new Date(startDate).getTime() !== new Date(schedule.startDate).getTime();
        /*
          If this is an update and we changed the RRULE or start dates, we need to do a refresh of schedules
          because the API would have ended the original schedule and generated a new one which is not returned
          from the patch api call
        */
        onClose(refreshData); // force schedule refresh if needed
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update the event. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  const handleSaveOccurrence = () => {
    setOpenSaveOptionsAnchorEl(false);
    if (!validateOccurrenceChangeFields()) { return; }

    const request = {
      startDate,
      endDate: startDate,
      duration,
      description: description || eventTemplate.description,
      assignment: assignment || '',
      name: (name && name.length >= 2)
        ? name
        : eventTemplate.name,
      tags: uniq([
        ...(workflows.map((w) => w.tagIds).flat()),
        ...additionalTags.map((et) => et.id),
        location.pingumTagId,
        ...barnHorses.map((bh) => bh.pingumTagId)]),
      workflows,
      location: detailedLocation || location.name,
      overrideCancellations: false, // TODO implement/change this
      metadata,
      type: 'Invitation',
    };

    const newChildScheduleRequest = {
      ...request,
      parenteScheduleId: (schedule.parentSchedule && schedule.parentSchedule.id) || schedule.id,
      rrule: 'FREQ=DAILY;INTERVAL=1',
    };

    // modify single occurrence in series
    if ((schedule.parentSchedule && schedule.parentSchedule.id)
      || (schedule.endDate && new Date(schedule.startDate).getTime() === new Date(schedule.endDate).getTime())) {
      // if this is a child or a parent but it does not repeat, single occurence, just modify the schedule
      dispatch(updateSchedule(schedule.id, newChildScheduleRequest))
        .then(() => {
          const refreshData = new Date(startDate).getTime() !== new Date(schedule.startDate).getTime();
          onClose(refreshData);
        }).catch((error) => {
          setSavingError(error.message || 'There was an error attempting to update the schedule. Please contact Questri support.');
          console.error(error);
        });
    } else {
      // add schedule exception for original occurrence time
      dispatch(addScheduleException(schedule.id, {
        exceptionDate: event.start,
        reason: 'Rescheduled occurrence',
        exceptionSchedule: newChildScheduleRequest,
      })).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update the schedule. Please contact Questri support.');
        console.error(error);
      });
    }
  }

  const disabledForBeingInPast = (schedule && event && event.start
    && new Date(event.start).getTime() < new Date().getTime()) && !overridePastSchedule

  return (
    // <Dialog
    //   TransitionComponent={Transition}
    //   open={open}
    //   PaperProps={{
    //     sx: {
    //       m: 0,
    //       minWidth: !fullscreen && 600,
    //     },
    //   }}
    //   fullScreen={fullscreen}
    //   onClose={() => onClose(false)}
    //   maxWidth="md"
    //   aria-labelledby="modal-add-schedule-title"
    //   aria-describedby="modal-add-schedule-description"
    // >
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: metadata.calendarDisplayColor || primary.main,
          color: getContrast(metadata.calendarDisplayColor || primary.main),
          m: 1,
          borderRadius: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Today
              sx={{
                height: 32,
                width: 32,
                mr: '10px',
                color: getContrast(metadata.calendarDisplayColor || primary.main),
              }}
            />
            <Box
              sx={{
                flex: 1,
                textWrap: 'auto',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '20px',
                }}
              >
                {(eventTemplate && eventTemplate.name) || (schedule && schedule.name) || 'Calendar Event'}
              </Box>
              {
                event && event.start && !overridePastSchedule && (

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <AccessTime
                      sx={{
                        height: 16,
                        width: 16,
                        mr: '5px',
                        color: getContrast(metadata.calendarDisplayColor || primary.main),
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 300,
                        color: getContrast(metadata.calendarDisplayColor || primary.main),
                      }}
                    >{Moment(event.start).format('LL')}
                    </Typography>
                  </Box>
                )
              }
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {
              schedule && schedule.id && (
                <EventActionsMenu
                  onClose={onClose}
                  sx={{ color: getContrast(metadata.calendarDisplayColor || primary.main) }}
                />
              )
            }
            <IconButton onClick={() => onClose(false)}>
              <Close sx={{ color: getContrast(metadata.calendarDisplayColor || primary.main) }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          paddingBottom: 0,
          p: 0,
        }}
      >
        {
          disabledForBeingInPast && (
            <Box
              sx={{
                textAlign: 'center',
                backgroundColor: blueGrey[50],
                borderRadius: 4,
                p: 2,
              }}
            >
              <Alert severity="info" sx={{ mb: 2 }}>
                This scheduled calendar event ended {Moment(new Date(schedule.endDate)).format('ll')}.
              </Alert>
              <Box>

                {
                  workflows && workflows.length > 0 && (
                    <Button
                      startIcon={<FormatListBulleted />}
                      variant="contained"
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={() => dispatch(setGlobalModals(TASK_MODAL_TYPE, null, schedule, event, {}, () => { }))}
                    >
                      View {workflows.length} Associated Task{workflows.length > 1
                        ? 's'
                        : ''}
                    </Button>
                  )
                }
                <Button startIcon={<EventRepeat />} size="small" variant="outlined" onClick={() => startNewSchedule()}>Reschedule Event</Button>
                <Button sx={{ ml: 1 }} size="small" onClick={() => { setOverridePastSchedule(false); onClose(false); }}>Cancel</Button>
              </Box>
            </Box>
          )
        }
        {
          disabledForBeingInPast && (

            <Divider
              sx={{
                mb: 2,
                mt: 1,
              }}
            />
          )
        }
        <HeaderSection
          schedule={schedule}
          assignment={assignment}
          setAssignment={setAssignment}
          name={name}
          validation={validation}
          setName={setName}
          description={description}
          setDescription={setDescription}
          duration={duration}
          setAdvanceNotice={setAdvanceNotice}
          advanceNotice={advanceNotice}
          workflows={workflows}
          metadata={metadata}
          setMetadata={setMetadata}
          location={location}
          handleChangeLocation={handleChangeLocation}
          barnHorses={barnHorses}
          handleChangeBarnHorses={setBarnHorses}
          additionalTags={additionalTags}
          setAdditionalTags={setAdditionalTags}
          detailedLocation={detailedLocation}
          setDetailedLocation={setDetailedLocation}
          open={open}
          selectedBarn={selectedBarn}
          disabledForBeingInPast={disabledForBeingInPast}
        />

        <Divider
          sx={{
            mb: 2,
            mt: 2,
          }}
        />

        {
          !eventTemplate
            ? (
              <>
                <Typography variant="captions" align="center" component="div" sx={{ m: 4 }}>
                  Select a calendar type first...
                </Typography>
                <img
                  src={SelectIcon}
                  alt="man selecting something"
                  style={{
                    width: 250,
                    display: 'block',
                    margin: 'auto',
                    marginBottom: '1rem',
                  }}
                />
              </>

            )
            : (
              <SchedulingSection
                validation={validation}
                schedule={schedule}
                event={event}
                startDate={startDate}
                endDate={endDate}
                duration={duration}
                eventTemplate={eventTemplate}
                rrule={rrule}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                setRrule={setRrule}
                setAdvanceNotice={setAdvanceNotice}
                setDuration={setDuration}
                disabledForBeingInPast={disabledForBeingInPast}
                timezoneWarning={timezoneWarning}
              />
            )
        }

        <Divider
          sx={{
            mb: 2,
            mt: 2,
          }}
        />
        {
          !disabledForBeingInPast && (
            <Box
              sx={{
                height: '100%',
                ml: 0,
                pl: 2,
              }}
            >
              <TasksSection
                eventTemplate={eventTemplate}
                workflows={workflows}
                validation={validation}
                setWorkflows={setWorkflows}
                taskTagIds={config.taskTagIds}
                setAdvanceNotice={setAdvanceNotice}
                advanceNotice={advanceNotice}
                barnHorses={barnHorses}
                location={location}
                disabledForBeingInPast={disabledForBeingInPast}
                setEventTemplate={setEventTemplate}
              />
            </Box>
          )
        }

        <Divider
          sx={{ mt: 2 }}
        />

        {
          ((!schedule || !schedule.id) && (new Date().getTime() - startDate.getTime() > 64800000)) && workflows.length > 0 && (
            <Alert variant="outlined" severity="warning" mt={2}>
              <AlertTitle>Scheduling a Retro Calendar Event</AlertTitle>
              <Box>
                You are scheduling a retroactive calendar event, configure how any past tasks for the event should behave:
              </Box>
              <FormGroup>
                <FormControlLabel sx={{ m: 0 }} control={<Checkbox checked={completeTasksInPast} onChange={(e) => setCompleteTasksInPast(e.target.checked)} />} label="Mark Past Task(s) Complete?" />
              </FormGroup>
            </Alert>
          )
        }

        {savingError && <Alert variant="outlined" severity="error">{savingError}</Alert>}
      </DialogContent>

      {!disabledForBeingInPast && (
        <DialogActions>
          <Button onClick={() => onClose(false)} disabled={isFetchingSchedules} sx={{ mr: 1 }}>Cancel</Button>
          <LoadingButton
            onClick={() => {
              const scheduleChangeData = getScheduleChangeData();
              if (scheduleChangeData.isNewSchedule || scheduleChangeData.isSingleOccurrenceSchedule
                || scheduleChangeData.isChangingFromSeriesToSingleOccurrence) {
                // If new schedule, single occurrence, or moving from series to single occurrence, we can just save
                handleSaveEntireSchedule()
              } else {
                setOpenSaveOptionsAnchorEl(true)
              }
            }}
            sx={{ flex: 1 }}
            disabled={isFetchingSchedules || !eventTemplate}
            variant="contained"
            color="primary"
            loading={isFetchingSchedules}
            loadingPosition="start"
            startIcon={<SaveIcon />}
          >
            Save
          </LoadingButton>
        </DialogActions>
      )}
      <Dialog
        open={openSaveOptionsAnchorEl}
        onClose={() => {
          setOpenSaveOptionsAnchorEl(false)
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Edit Recurring Calendar Event
        </DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          {
            schedule && schedule.id && startDate && (
              !scheduleRepeatChange()
                ? (
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      defaultValue="female"
                      name="radio-buttons-group"
                    >
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={(
                          <Radio
                            checked={selectedSaveOption === 'event'}
                            onClick={() => {
                              setSelectedSaveOption('event')
                            }}
                          />
                        )}
                        label={`This Calendar Event (${Moment(startDate).format('ll')})`}
                      />
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={(
                          <Radio
                            checked={selectedSaveOption === 'all'}
                            onClick={() => {
                              setSelectedSaveOption('all')
                            }}
                          />
                        )}
                        label={<span>&#42;All Calendar Events</span>}
                      />
                    </RadioGroup>
                    <FormHelperText>
                      &#42;Only scheduled (future) events will be changed.
                      Calendar Events that are in the past will not be modified.
                    </FormHelperText>
                  </FormControl>
                )
                : (
                  <Alert severity="info">
                    You are changing the frequency and or end date on this repeating calendar event.
                    All future occurrences will be modified.
                  </Alert>
                )
            )
          }

        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() => { setOpenSaveOptionsAnchorEl(false) }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!selectedSaveOption && !scheduleRepeatChange()}
            sx={{ ml: 1 }}
            onClick={() => {
              if (scheduleRepeatChange() || selectedSaveOption === 'all') {
                handleSaveEntireSchedule()
              } else {
                handleSaveOccurrence()
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    // </Dialog>
  );
};

AddEditScheduleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  schedule: PropTypes.object,
  event: PropTypes.object,
  config: PropTypes.object,
};

AddEditScheduleModal.defaultProps = {
  schedule: null,
  event: null,
  config: {},
};

export default AddEditScheduleModal;
